import { useSearchParams } from 'next/navigation';
import { useMemo, useState } from 'react';
import { ExtendedProductDetailProduct, FinishData } from 'shared/types/product/Product';
import { FINISH_QUERY_PARAM, SHEEN_QUERY_PARAM } from 'helpers/constants/query-params';
import { useSetQueryParam } from 'helpers/hooks/useSetQueryParam';

export const useFinishAndSheens = (product: ExtendedProductDetailProduct) => {
  const searchParams = useSearchParams();
  const { setQueryParam } = useSetQueryParam();

  const finishOptionsSorted = useMemo(
    () => (product.finishes ? Object.values(product.finishes).sort((a, b) => (a.name > b.name ? 1 : -1)) : undefined),
    [product],
  );

  const sheensOptionsSorted = useMemo(() => product.sheens?.toSorted(), [product]);

  const [finish, setFinish] = useState<FinishData | undefined>(() => {
    const finishParam = searchParams.get(FINISH_QUERY_PARAM);
    if (finishParam) {
      const existingFinish = product.finishes?.[finishParam];
      if (existingFinish) return existingFinish;
    }
    return finishOptionsSorted?.[0];
  });

  const [sheen, setSheen] = useState<string | undefined>(() => {
    const sheenParam = searchParams.get(SHEEN_QUERY_PARAM);
    if (sheenParam) {
      const existingSheen = sheensOptionsSorted?.find((s) => s === sheenParam) ?? undefined;
      if (existingSheen) return existingSheen;
    }
    return sheensOptionsSorted?.[0];
  });

  const onChangeFinish = (finishLabel: string) => {
    if (!product.finishes) return;
    const foundFinish = Object.entries(product.finishes).find(
      ([_finishKey, finishData]) => finishData.name === finishLabel,
    );
    if (foundFinish) {
      setFinish(foundFinish[1]);
      setQueryParam(FINISH_QUERY_PARAM, foundFinish[0]);
    }
  };

  const onChangeSheen = (sheen: string) => {
    setSheen(sheen);
    setQueryParam(SHEEN_QUERY_PARAM, sheen);
  };

  return { finish, onChangeFinish, sheen, onChangeSheen, finishes: finishOptionsSorted, sheens: sheensOptionsSorted };
};
